import {
  Calculator,
  GraduationCap,
  NotebookPen,
  ShoppingBag,
  TrendingUp,
  Users,
} from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";

const Visual = ({ kind }) => {
  switch (kind) {
    case "calculator":
      return (
        <div className="flex items-end gap-3" aria-hidden="true">
          <div className="flex-1">
            <div className="h-10 rounded-lg bg-splurge-primary/85" />
            <p className="mt-1.5 text-[10px] font-semibold text-splurge-ink/50">₹450 out</p>
          </div>
          <div className="flex-1">
            <div className="h-16 rounded-lg bg-splurge-lavender" />
            <p className="mt-1.5 text-[10px] font-semibold text-splurge-ink/50">3 movie nights</p>
          </div>
        </div>
      );
    case "ledger":
      return (
        <div className="space-y-1.5" aria-hidden="true">
          <div className="flex justify-between rounded-lg bg-splurge-bg px-3 py-1.5 text-[11px] font-semibold text-splurge-ink/70">
            <span>Dinner</span><span>₹450</span>
          </div>
          <div className="flex justify-between rounded-lg bg-splurge-bg px-3 py-1.5 text-[11px] font-semibold text-splurge-ink/70">
            <span>Cab</span><span>₹180</span>
          </div>
          <div className="flex justify-between rounded-lg bg-splurge-lavender px-3 py-1.5 text-[11px] font-bold text-splurge-dark">
            <span>This week</span><span>₹1,240</span>
          </div>
        </div>
      );
    case "group":
      return (
        <div className="flex items-center justify-between" aria-hidden="true">
          <div className="flex -space-x-2">
            {["R", "A", "S", "M"].map((n) => (
              <span
                key={n}
                className="grid h-8 w-8 place-items-center rounded-full border-2 border-white bg-splurge-violet/80 text-[11px] font-extrabold text-white"
              >
                {n}
              </span>
            ))}
          </div>
          <span className="rounded-full bg-splurge-lavender px-2.5 py-1 text-[10px] font-extrabold text-splurge-dark">
            2 settled
          </span>
        </div>
      );
    case "wishlist":
      return (
        <div className="flex flex-wrap items-center gap-2" aria-hidden="true">
          <span className="rounded-full bg-splurge-ink px-3 py-1.5 text-[11px] font-bold text-white">
            New shoes ₹2,999
          </span>
          <span className="text-[11px] font-bold text-splurge-ink/40">vs</span>
          <span className="rounded-full bg-splurge-lavender px-3 py-1.5 text-[11px] font-bold text-splurge-dark">
            Eating out ₹1,900
          </span>
        </div>
      );
    case "learning":
      return (
        <div aria-hidden="true">
          <div className="h-2 w-full overflow-hidden rounded-full bg-splurge-bg">
            <div className="h-full w-[68%] rounded-full bg-gradient-to-r from-splurge-primary to-splurge-violet" />
          </div>
          <p className="mt-2 text-[11px] font-semibold text-splurge-ink/50">
            68% · 3 videos finished with friends
          </p>
        </div>
      );
    case "insights":
      return (
        <div className="flex items-center gap-3" aria-hidden="true">
          <svg viewBox="0 0 80 28" className="h-7 w-20">
            <polyline
              points="0,24 14,18 28,20 42,10 56,13 70,4 80,7"
              fill="none"
              stroke="#7017EE"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="rounded-full bg-splurge-lavender px-2.5 py-1 text-[10px] font-extrabold text-splurge-dark">
            India trends
          </span>
        </div>
      );
    default:
      return null;
  }
};

const FEATURES = [
  {
    number: "01",
    slug: "impact-calculator",
    icon: Calculator,
    visual: "calculator",
    title: "Impact Calculator",
    copy: "Log daily, weekly, and monthly expenses, then compare them with things you could have bought instead.",
  },
  {
    number: "02",
    slug: "spending-ledger",
    icon: NotebookPen,
    visual: "ledger",
    title: "Spending Ledger",
    copy: "Keep a simple expense log with clear weekly and monthly summaries, no complicated setup.",
  },
  {
    number: "03",
    slug: "group-settle",
    icon: Users,
    visual: "group",
    title: "Group Settle",
    copy: "Track shared expenses, see who owes whom, and manage a group budget with friends.",
  },
  {
    number: "04",
    slug: "wishlist-comparison",
    icon: ShoppingBag,
    visual: "wishlist",
    title: "Wishlist Comparison",
    copy: "Compare what you want to buy with what you are actually spending to make more intentional choices.",
  },
  {
    number: "05",
    slug: "learning-resources",
    icon: GraduationCap,
    visual: "learning",
    title: "Learning Resources",
    copy: "Access financial-literacy videos and articles, then earn points for learning with friends.",
  },
  {
    number: "06",
    slug: "student-first-insights",
    icon: TrendingUp,
    visual: "insights",
    title: "Student-first Insights",
    copy: "Get informational, AI-based comparisons focused on Indian trends and student life.",
  },
];

export const Features = () => {
  return (
    <section id="features" className="scroll-mt-20 bg-splurge-bg py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-splurge-primary">
            Inside the app
          </p>
        </Reveal>
        <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal delay={80}>
            <h2 className="max-w-2xl text-4xl font-extrabold leading-[1.08] tracking-tight text-splurge-ink sm:text-5xl">
              Everything you need to make money feel{" "}
              <span className="text-splurge-primary">simpler.</span>
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="max-w-md text-base leading-relaxed text-splurge-ink/60 md:text-lg">
              Useful tools for everyday spending, built around the way students actually live.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature, i) => (
            <Reveal key={feature.number} delay={(i % 3) * 110}>
              <article
                data-testid={`feature-card-${feature.slug}`}
                className="group flex h-full flex-col rounded-3xl border border-splurge-lavender bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-splurge-violet/50 hover:shadow-xl hover:shadow-splurge-primary/10"
              >
                <div className="flex items-start justify-between">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-splurge-lavender transition-colors duration-300 group-hover:bg-splurge-primary">
                    <feature.icon
                      className="h-5 w-5 text-splurge-primary transition-colors duration-300 group-hover:text-white"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="text-xs font-extrabold tracking-widest text-splurge-ink/25">
                    {feature.number}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-extrabold tracking-tight text-splurge-ink">
                  {feature.title}
                </h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-splurge-ink/60">
                  {feature.copy}
                </p>
                <div className="mt-6 border-t border-splurge-lavender/80 pt-5">
                  <Visual kind={feature.visual} />
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
